const toArray = (value) => (Array.isArray(value) ? value : []);
const normalizeText = (value) => (typeof value === "string" ? value.trim() : "");
const normalizeBoolean = (value, fallback = false) => {
  if (value === undefined || value === null || value === "") return fallback;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["true", "1", "yes", "on"].includes(normalized)) return true;
    if (["false", "0", "no", "off"].includes(normalized)) return false;
  }
  return Boolean(value);
};

export const normalizePublicBookingBenefitsConfig = (value = {}) => {
  const loyaltyRewardsEnabled = normalizeBoolean(value?.loyaltyRewardsEnabled, true);
  const couponsEnabled = normalizeBoolean(value?.couponsEnabled, true);
  return {
    savingsPreviewEnabled: normalizeBoolean(value?.savingsPreviewEnabled, false),
    loyaltyRewardsEnabled,
    ecoCoinsEnabled: normalizeBoolean(value?.ecoCoinsEnabled, loyaltyRewardsEnabled),
    couponsEnabled,
    availableCouponsEnabled: couponsEnabled && normalizeBoolean(value?.availableCouponsEnabled, true),
    featureFlags: value?.featureFlags && typeof value.featureFlags === "object" ? value.featureFlags : {},
  };
};

export const normalizeHostedFormsConfig = (value = []) =>
  toArray(value)
    .map((item) => {
      const slug = normalizeText(item?.slug);
      const title = normalizeText(item?.title || item?.label);
      if (!slug || !title) return null;
      const publicPath = normalizeText(item?.publicPath) || `/forms/${slug}`;
      return {
        id: normalizeText(item?.id) || slug,
        slug,
        title,
        summary: normalizeText(item?.summary),
        publicPath,
        visibilityWindow:
          item?.visibilityWindow && typeof item.visibilityWindow === "object"
            ? {
                startDate: normalizeText(item.visibilityWindow.startDate),
                endDate: normalizeText(item.visibilityWindow.endDate),
              }
            : { startDate: "", endDate: "" },
      };
    })
    .filter(Boolean);

const normalizeContentNavItem = (item = {}) => {
  const slug = normalizeText(item?.slug);
  const label = normalizeText(item?.label || item?.name);
  const publicPath = normalizeText(item?.publicPath) || (slug ? `/${slug}` : "");
  if (!slug || !label || !publicPath) return null;
  return {
    id: normalizeText(item?.id) || slug,
    slug,
    label,
    name: normalizeText(item?.name) || label,
    description: normalizeText(item?.description),
    publicPath,
    showInPrimaryNav: Boolean(item?.showInPrimaryNav),
    showInFooter: Boolean(item?.showInFooter),
    sortOrder: Number.isFinite(Number(item?.sortOrder)) ? Number(item.sortOrder) : 0,
  };
};

export const normalizeContentNavigationConfig = (value = {}) => {
  const sourceItems = Array.isArray(value?.items) ? value.items : [];
  const normalizedItems = sourceItems.map((item) => normalizeContentNavItem(item)).filter(Boolean);
  const byPath = new Map(normalizedItems.map((item) => [item.publicPath, item]));
  const resolveSubset = (items = [], predicate) => {
    const normalized = (Array.isArray(items) ? items : [])
      .map((item) => normalizeContentNavItem(item))
      .filter(Boolean)
      .map((item) => byPath.get(item.publicPath) || item);
    const fallback = normalizedItems.filter(predicate);
    const source = normalized.length ? normalized : fallback;
    return source.filter(
      (item, index, arr) => arr.findIndex((candidate) => candidate.publicPath === item.publicPath) === index,
    );
  };

  return {
    items: normalizedItems,
    primaryLinks: resolveSubset(value?.primaryLinks, (item) => item.showInPrimaryNav),
    footerLinks: resolveSubset(value?.footerLinks, (item) => item.showInFooter),
  };
};

export const normalizeNavigationConfig = (config = {}) => {
  const nightStayColumns = toArray(config.nightStayColumns);
  const nightStayList = Array.isArray(config.nightStayList) && config.nightStayList.length
    ? config.nightStayList
    : nightStayColumns.flat().filter(Boolean);

  return {
    nightStayColumns,
    nightStayList,
    dayDestinations: toArray(config.dayDestinations),
    jungleSafariParks: toArray(config.jungleSafariParks),
    cruiseOptions: toArray(config.cruiseOptions),
    bookingMenuSections: toArray(config.bookingMenuSections),
    contactMenuLinks: toArray(config.contactMenuLinks),
  };
};
